
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $getSelection, $isRangeSelection } from 'lexical'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { NodeType } from '../nodes/NodeCore'
import { getCurrentNode, hasTextVisibleTextAfterCaret } from '../utils/NodeUtils'
import { BaseNode } from '../nodes/BaseNode'
import { KEYS } from '@/components/custom/editor/stringKeys/StringKeys'
import { useScreenplaySuggestionMutation } from '@/features/screenplay/queries/screenplay.ai.queries'

function shouldSuggest(currentLine: string, currentNode: NodeType) {
    if ([NodeType.Character, NodeType.Transition].includes(currentNode)) {
        return false
    }
    return true
}
export function SpAutoCompletePlugin() {
    const [editor] = useLexicalComposerContext()
    const { mutateAsync } = useScreenplaySuggestionMutation()
    const [suggestion, setSuggestion] = useState('')
    const [show, setShow] = useState(false)
    const [position, setPosition] = useState<{ top: number; left: number; right: number } | null>(
        null
    )
    const DEBOUNCE_MS = 450 // wait for pause
    const MIN_INTERVAL_MS = 0 // optional: hard rate limit
    const lastSigRef = useRef<string>('') // last request signature
    const lastCallAtRef = useRef<number>(0) // last call timestamp
    const timerRef = useRef<number | null>(null) // debounce timer id
    const reqSeqRef = useRef(0) // request sequence counter

    function getCursorPosition(): DOMRect | null {
        const selection = window.getSelection()
        if (!selection || selection.rangeCount === 0) return null
        const range = selection.getRangeAt(0)
        if (!range.collapsed) return null
        // Try to get a rect representing the caret position
        const rects = range.getClientRects()
        if (rects.length > 0) {
            return rects[0] // this is the most accurate position of the caret
        }
        // Fallback to startContainer if range doesn't return rects
        const container = range.startContainer
        const referenceNode =
            container.nodeType === Node.TEXT_NODE ? container.parentElement : (container as Element)

        if (referenceNode instanceof Element) {
            return referenceNode.getBoundingClientRect()
        }

        return null
    }

    useEffect(() => {
        return editor.registerUpdateListener(({ editorState }) => {
            editorState.read(() => {
                const currentNode = getCurrentNode() as BaseNode
                if (!currentNode) return

                const caretPositionSafe = !hasTextVisibleTextAfterCaret(currentNode)
                const currentText = currentNode.getTextContent()
                const nodeType = currentNode.getType()
                const prevContent = currentNode.getPreviousTextContent()

                // Only suggest if your own rule passes AND caret is at a “safe” spot
                if (!shouldSuggest(currentText, nodeType as NodeType) || !caretPositionSafe) {
                    clearTimer()
                    setShow(false)
                    setSuggestion('')
                    return
                }

                // ---- Debounce: schedule after a small pause ----
                clearTimer()
                timerRef.current = window.setTimeout(async () => {
                    // ---- Signature guard: only call when inputs changed ----
                    const sig = `${nodeType}||${prevContent}||${currentText}`
                    if (sig === lastSigRef.current) return

                    // ---- Hard rate limit (optional) ----
                    const now = Date.now()
                    if (now - lastCallAtRef.current < MIN_INTERVAL_MS) return

                    lastSigRef.current = sig
                    lastCallAtRef.current = now

                    // ---- Stale response guard ----
                    const mySeq = ++reqSeqRef.current

                    const suggestionText = await mutateAsync({
                        prevContent,
                        nodeType: nodeType as string,
                        currentText,
                    }).catch(() => '')

                    // Ignore late/stale responses
                    if (mySeq !== reqSeqRef.current) return

                    // Show or hide suggestion
                    if (suggestionText) {
                        setSuggestion(suggestionText)
                        setShow(true)

                        const contentEditableDom = document.getElementById(KEYS.SP_CONTENT_EDITABLE)
                        const editableRect = contentEditableDom?.getBoundingClientRect()
                        const right = editableRect?.right
                        const rect = getCursorPosition()
                        if (rect) {
                            setPosition({ top: rect.top, left: rect.left, right: right! })
                        }
                    } else {
                        setShow(false)
                        setSuggestion('')
                    }
                }, DEBOUNCE_MS)
            })
        })

        function clearTimer() {
            if (timerRef.current) {
                clearTimeout(timerRef.current)
                timerRef.current = null
            }
        }
    }, [editor])

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (!show) return

            if (e.key === 'Tab') {
                e.preventDefault()
                editor.update(() => {
                    const selection = $getSelection()
                    if ($isRangeSelection(selection)) {
                        selection.insertText(suggestion)
                    }
                })
                setSuggestion('')
                setShow(false)
            } else {
                setSuggestion('')
                setShow(false)
            }
        }

        document.addEventListener('keydown', handler)
        return () => document.removeEventListener('keydown', handler)
    }, [editor, show, suggestion])
    //console.log(show, suggestion, position)
    return show && suggestion && position
        ? createPortal(
            <div
                style={{
                    position: 'absolute',
                    top: position.top + window.scrollY,
                    left: position.left + window.scrollX,
                    borderRadius: 4,
                    fontSize: 14,
                    width: position.right - position.left,
                    //right: position.right + window.scrollX,
                    color: '#666',
                    opacity: 0.6,
                    pointerEvents: 'none',
                }}
                className=""
            >
                <span className="text-slate-900 ">&nbsp;⇥&nbsp;</span>
                <span className="">{suggestion}</span>
            </div>,
            document.body
        )
        : null
}
